import { SpanStatusCode } from '@opentelemetry/api'

import { remoteLog } from '$extensionSrc/utils/remoteLog'
import { withSpan } from '$extensionSrc/utils/tracing'

import {
  getAttrPromise,
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'

async function getSwitchEl(
  section: string,
  label: string,
): Promise<HTMLElement | null> {
  return withSpan(
    'reddit.switchUtils.getSwitchEl',
    async (span) => {
      const faceplateSwitchInput =
        await getWaitForElementToAppearPromise<HTMLElement>(
          document,
          () => {
            const sectionElement = document.querySelector(section)
            if (sectionElement) {
              return sectionElement.shadowRoot.querySelector(
                `faceplate-switch-input[aria-label="${label}"]`,
              )
            }
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: `getSwitchEl: Couldn't find section element for section ${section}`,
            })
            return null
          },
          5000,
        )
      if (!faceplateSwitchInput) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: `getSwitchEl: Couldn't find faceplate-switch-input for label ${label}`,
        })
        return null
      }
      return faceplateSwitchInput
    },
    {
      section,
      label,
    },
  )
}

async function getSwitchElOld(oldLabel: string): Promise<HTMLElement | null> {
  return withSpan(
    'reddit.switchUtils.getSwitchElOld',
    async (span) => {
      const h3El = await getWaitForElementToAppearPromise<HTMLHeadingElement>(
        document,
        (node) =>
          evaluateXpath<HTMLHeadingElement>(
            node,
            `//h3[text()="${oldLabel}"]`,
          )[0],
        5000,
      )
      if (!h3El) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: `getSwitchElOld: Couldn't find h3 for oldLabel ${oldLabel}`,
        })
        return null
      }
      if (!h3El.parentElement) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: `getSwitchElOld: No parentElement for h3 with oldLabel ${oldLabel}`,
        })
        return null
      }
      const id = h3El.parentElement.getAttribute('for')
      const buttonEl = await getWaitForElementToAppearPromise<HTMLElement>(
        document,
        (node: Document) => node.getElementById(id),
        5000,
      )
      if (buttonEl?.tagName !== 'BUTTON') {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: `getSwitchElOld: Couldn't find button with ID ${id} for oldLabel ${oldLabel}`,
        })
        return null
      }
      return buttonEl
    },
    {
      oldLabel,
    },
  )
}

export async function getSwitchValue(
  section: string,
  label: string,
  oldLabel: string,
): Promise<boolean> {
  let el: HTMLElement | null = await getSwitchEl(section, label)
  if (!el) {
    el = await getSwitchElOld(oldLabel)
  }
  if (!el) {
    const message = `getSwitchValue: Could not find el for section ${section}, label ${label}, and oldLabel ${oldLabel}`
    remoteLog.error(message)
    throw new Error(message)
  }
  return el.ariaChecked === 'true'
}

export async function toggleSwitch(
  section: string,
  label: string,
  oldLabel: string,
): Promise<void> {
  let el: HTMLElement | null = await getSwitchEl(section, label)
  if (!el) {
    el = await getSwitchElOld(oldLabel)
  }
  if (!el) {
    const message = `toggleSwitch: Could not find el for section ${section}, label ${label}, and oldLabel ${oldLabel}`
    remoteLog.error(message)
    throw new Error(message)
  }
  el.click()
  await getAttrPromise(el, 'aria-checked')
  await wait(250)
}
